import { AxiosResponse, AxiosError } from 'axios';
import jwt_decode from 'jwt-decode';
import UserApi from '@/modules/user/services/UserApi';
import EnvironmentService from '@/env/EnvironmentService';
import { RoleTypes } from '@/commons/enums/RoleTypes';
import AppRouter, { getIndexRouteName } from '@/router';
import { Routes } from '@/router/Routes';
import UserCom from '@/services/log/UserCom';
import NotificationService from '@/modules/notification/NotificationService';
import store from '@/store/store';
import Logger from '@/services/log/Logger';
import {
  TOKEN_GETTER_KEY,
  TOKEN_TYPE_KEY,
  TokenType,
  ZITADEL_ID_TOKEN_KEY,
  ZITADEL_LOGIN_NAME_KEY,
  AUTH_MODE_KEY,
  AuthMode,
} from '@/commons/enums/TokenType';
import { Route } from 'vue-router';
import GoToService from '@/router/GoToService';
import { LoginData } from '@/models/LoginData';
import JwtData from '@/models/user/JwtData';
import { keys, merge } from 'lodash-es';
import Vue, { ref } from 'vue';
import { ProductType } from '@/commons/enums/ProductType';
import { Role } from '@/models/user/Role';
import { IUser } from '@/models/user/IUser';
import { I18N } from '@/services/enumTranslator/I18NDictionary';
import { LoginDataResponseDTO } from '@/models/user/LoginDataResponseDTO';
import { I18NGetter } from '@/services/enumTranslator/I18NGetter';
import { useZitadelAuth } from '@/composables/useZitadelAuth';
import { useAccessCookie } from '@/composables/useAccessCookie';
import type { User as OidcUser } from 'oidc-client-ts';
import { wait } from '@/services/utils/BasicUtils';
import User from '@/models/user/User';
import { useActivityStore } from '@/modules/activities/services/ActivityStore';
import { useModalStore } from '@/components/L/Modal/useModalStore';
import pinia from '@/store/pinia';
import { crossTabRenewCoordinator } from '@/services/auth/CrossTabRenewCoordinator';

const LAST_PATH_KEY = 'LAST_PATH';
const RENEW_FAILURE_COOLDOWN_MS = 2000;
const CROSS_TAB_RENEW_WAIT_MS = 12000;

type ZitadelTokenClaims = {
  preferred_username?: string;
  login_name?: string;
  username?: string;
  email?: string;
};

type JwtExpirationClaim = {
  exp?: number;
};

export const TABLE_FILTER_KEYS = {
  LAST_REPORT: 'LAST_REPORT_TABLE_FILTER',
  REGULATIONS_LIST: 'REGULATIONS_LIST_TABLE_FILTER',
  LAST_LOAN: 'LAST_LOAN_TABLE_FILTER',
  LAST_CLIENT: 'LAST_CLIENT_TABLE_FILTER',
  LAST_DEALS: 'LAST_DEALS_TABLE_FILTER',
  LAST_KANBAN_DEALS: 'LAST_KANBAN_DEALS',
  LAST_PROSPECT: 'LAST_PROSPECT_TABLE_FILTER',
  LAST_TRAININGS: 'LAST_TRAININGS_TABLE_FILTER',
  LAST_FILEMANAGER_CATEGORIES: 'LAST_FILEMANAGER_CATEGORIES_TABLE_FILTER',
  LAST_DIVISIONS: 'LAST_DIVISIONS_TABLE_FILTER',
};

class AuthService {
  private zitadelAuthService: ReturnType<typeof useZitadelAuth>;
  private renewTokenPromise: Promise<OidcUser | null> | null = null;
  private lastRenewFailureAt: number | null = null;

  constructor() {
    this.zitadelAuthService = useZitadelAuth();
    this.zitadelAuthService.onTokenObtaining(this.handleTokenObtaining.bind(this));
  }

  public async signingInRedirect(userName?: string): Promise<void> {
    return this.zitadelAuthService.signingInRedirect(userName);
  }

  public async signingInRedirectSelectAccount(): Promise<void> {
    return this.zitadelAuthService.signingInRedirectSelectAccount();
  }

  public async checkAuthMethod(userName: string) {
    try {
      return await UserApi.checkAuthMethod(userName);
    } catch (error) {
      Vue.prototype.errorHandler(error, "Błąd sprawdzania metody uwierzytelniania");
      throw error;
    }
  }

  public get tokenCallback() {
    return this.zitadelAuthService.tokenCallback;
  }

  /**
   * Subscribe to every token the OIDC layer hands us — the initial sign-in and
   * each silent renew.
   *
   * Wraps `userManager.events.addUserLoaded`, which exposes no unsubscribe
   * here, so a subscription lives for the lifetime of the app. Subscribe once
   * at module level and fan out rather than subscribing per component.
   */
  public onTokenObtained(action: (user: OidcUser) => void): void {
    this.zitadelAuthService.onTokenObtaining(action);
  }

  public async renewToken(): Promise<OidcUser | null> {
    if (!this.isZitadelAuthMode) {
      return null;
    }

    if (
      this.lastRenewFailureAt !== null
      && Date.now() - this.lastRenewFailureAt < RENEW_FAILURE_COOLDOWN_MS
    ) {
      return null;
    }

    if (this.renewTokenPromise) {
      return this.renewTokenPromise;
    }

    if (crossTabRenewCoordinator.hasActiveLock()) {
      await this.waitForCrossTabRenew();
      return null;
    }

    const lockResult = crossTabRenewCoordinator.acquireLock();
    if (!lockResult.acquired || !lockResult.lockId) {
      await this.waitForCrossTabRenew();
      return null;
    }

    const lockId = lockResult.lockId;
    const stopLockHeartbeat = crossTabRenewCoordinator.startHeartbeat(lockId);

    this.renewTokenPromise = (async () => {
      try {
        const userManager = this.zitadelAuthService.userManager;
        if (!userManager) {
          console.warn('Silent renew skipped: OIDC userManager is not initialized');
          this.lastRenewFailureAt = Date.now();
          crossTabRenewCoordinator.publishFailure(lockId);
          return null;
        }

        const user = await userManager.signinSilent();
        if (user) {
          this.lastRenewFailureAt = null;
          /** UserLoaded → handleTokenObtaining (cookie + /info). Avoid duplicate handleTokenObtaining here — it caused nested /info 401 → renew loops. */
          crossTabRenewCoordinator.publishSuccess(lockId);
          return user;
        }
        this.lastRenewFailureAt = Date.now();
        crossTabRenewCoordinator.publishFailure(lockId);
        return null;
      } catch (e) {
        console.error('Silent renew failed', e);
        this.lastRenewFailureAt = Date.now();
        crossTabRenewCoordinator.publishFailure(lockId);
        return null;
      } finally {
        stopLockHeartbeat();
        crossTabRenewCoordinator.releaseLock(lockId);
        this.renewTokenPromise = null;
      }
    })();

    return this.renewTokenPromise;
  }

  public async waitForCrossTabRenew(timeoutMs: number = CROSS_TAB_RENEW_WAIT_MS): Promise<boolean> {
    if (!this.isZitadelAuthMode || !crossTabRenewCoordinator.isEnabled()) {
      return false;
    }

    const waitResult = await crossTabRenewCoordinator.waitForRenewResult(timeoutMs);
    if (waitResult === 'timeout') {
      console.warn('Cross-tab token renew wait timed out');
    } else if (waitResult === 'failed') {
      console.warn('Cross-tab token renew failed in another tab');
    }
    return waitResult === 'success' || waitResult === 'released';
  }

  public get isAutomaticRenewInProgress(): boolean {
    return this.isZitadelAuthMode && this.zitadelAuthService.isAutomaticRenewInProgress();
  }

  public async waitForAutomaticRenew(timeoutMs: number = 1500): Promise<OidcUser | null> {
    if (!this.isAutomaticRenewInProgress) {
      if (crossTabRenewCoordinator.hasActiveLock()) {
        await this.waitForCrossTabRenew(timeoutMs);
      }
      return null;
    }

    const automaticRenewPromise = this.zitadelAuthService.waitForAutomaticRenew();
    if (!automaticRenewPromise) {
      return null;
    }

    return Promise.race([
      automaticRenewPromise,
      wait(timeoutMs).then(() => null),
    ]);
  }

  public handleTokenObtaining = async (oidcUser?: OidcUser): Promise<void> => {

    try {
      const authResponse: OidcUser = oidcUser ?? await this.tokenCallback();
      /**@Info to pass expires_in to useAccessCookie we need to use the composable useAccessCookie again, aside one in constructor to generate a correct setter.
       * It doesn't influence the gettter response, because it is all based on the document.cookie so there can be two instances of useAccessCookie.
       */
      const accessCookie = useAccessCookie(authResponse.expires_in);

      accessCookie.value = authResponse.access_token;
      this.persistZitadelIdentity(authResponse);
      this.authMode = AuthMode.ZITADEL;
      localStorage.setItem(TOKEN_TYPE_KEY, TokenType.EXPERT);
      await wait(10);

      const loginDataResponse = await this.fetchUserInfo();
      const authRoutes = ['mfa/login', 'auth/logout/callback', 'auth/callback'];
      const isOnAuthRoute = authRoutes.some(path => AppRouter.currentRoute.path.includes(path));

      if (isOnAuthRoute) {
        // Initial login — navigate to the app
        this.handleUserState(loginDataResponse);
      } else {
        // Silent renewal — fetchUserInfo() already updated this.User with proper normalization
        if (loginDataResponse.activities?.hasExpired) {
          AppRouter.push({ name: Routes.BLOCKED_ACCESS });
        }
      }
    } catch (e) {
      Vue.prototype.errorHandler(e, "Błąd podczas uzyskiwania informacji o użytkowniku");
    }
  };

  private get authMode(): AuthMode | null {
    return localStorage.getItem(AUTH_MODE_KEY) as AuthMode | null;
  }

  private get resolvedAuthMode(): AuthMode | null {
    const hasAccessToken = !!this.accessToken;
    const hasOldToken = !!this.oldToken;

    if (hasAccessToken && !hasOldToken) {
      return AuthMode.ZITADEL;
    }

    if (hasOldToken && !hasAccessToken) {
      return AuthMode.LEGACY;
    }

    if (hasAccessToken && hasOldToken) {
      const storedAuthMode = this.authMode;
      if (storedAuthMode === AuthMode.ZITADEL || storedAuthMode === AuthMode.LEGACY) {
        return storedAuthMode;
      }

      console.warn('Ambiguous auth mode detected (both access and legacy tokens present). Falling back to zitadel mode.');
      return AuthMode.ZITADEL;
    }

    // No tokens in memory/cookies — fall back to persisted mode so that
    // isZitadelAuthMode stays stable even when the access cookie has expired
    // and we still need to attempt a silent token renewal.
    return this.authMode;
  }

  public get isZitadelAuthMode(): boolean {
    return this.resolvedAuthMode === AuthMode.ZITADEL;
  }

  public get isLegacyAuthMode(): boolean {
    return this.resolvedAuthMode === AuthMode.LEGACY;
  }

  private set authMode(mode: AuthMode | null) {
    if (mode) {
      localStorage.setItem(AUTH_MODE_KEY, mode);
    } else {
      localStorage.removeItem(AUTH_MODE_KEY);
    }
  }

  public fetchUserInfo = async (): Promise<LoginDataResponseDTO> => {
    const response = await UserApi.userInfoFull();
    this.User = new User(response.user, response.privileges, response.quiz);
    return response;
  }

  public get accessToken(): string | undefined {
    return useAccessCookie().value;
  }

  /** @Info mfa access token */
  public set accessToken(newToken: string | undefined) {
    useAccessCookie().value = newToken;
  }

  public get zitadelLoginName(): string | undefined {
    if (!this.accessToken) {
      return this.User?.username || this.User?.email || undefined;
    }

    const idTokenLoginName = this.getLoginNameFromIdToken(this.getStoredZitadelIdToken());
    const storedLoginName = localStorage.getItem(ZITADEL_LOGIN_NAME_KEY) || undefined;

    return idTokenLoginName || storedLoginName || this.User?.username || this.User?.email || undefined;
  }

  private getLoginNameFromIdToken(idToken?: string): string | undefined {
    if (!idToken) {
      return undefined;
    }

    try {
      // Used only for UX hints (login prefill), not for security decisions.
      const decoded = jwt_decode<ZitadelTokenClaims>(idToken);
      const loginName =
        decoded.preferred_username ||
        decoded.login_name ||
        decoded.username ||
        decoded.email;

      return typeof loginName === 'string' && loginName.trim().length > 0
        ? loginName
        : undefined;
    } catch {
      return undefined;
    }
  }

  private getStoredZitadelIdToken(): string | undefined {
    return sessionStorage.getItem(ZITADEL_ID_TOKEN_KEY) || undefined;
  }

  private getLoginNameFromOidcProfile(oidcUser: OidcUser): string | undefined {
    const profile = oidcUser.profile as Partial<ZitadelTokenClaims> | undefined;
    if (!profile) {
      return undefined;
    }

    const loginName =
      profile.preferred_username ||
      profile.login_name ||
      profile.username ||
      profile.email;

    return typeof loginName === 'string' && loginName.trim().length > 0
      ? loginName
      : undefined;
  }

  private persistZitadelIdentity(oidcUser: OidcUser): void {
    if (oidcUser.id_token) {
      sessionStorage.setItem(ZITADEL_ID_TOKEN_KEY, oidcUser.id_token);
    }

    const loginName =
      this.getLoginNameFromIdToken(oidcUser.id_token) ||
      this.getLoginNameFromOidcProfile(oidcUser);

    if (loginName) {
      localStorage.setItem(ZITADEL_LOGIN_NAME_KEY, loginName);
    }
  }

  private UserData: Nullable<IUser> = null;
  /** Reactive identity key, format `uuid|id|organisationId` (empty when logged
   * out). Changes on login, account switch, organisation switch, and logout.
   * Watch this from components that must react to identity changes (e.g. the
   * preloaded iframe, which needs to reload with fresh auth/org tokens). */
  public readonly userStateV2Key = ref<string>('');
  /** @Info mfa access token or old token JWT */
  public get token(): string | null {
    if (this.isZitadelAuthMode) {
      return this.accessToken || null;
    }
    if (this.isLegacyAuthMode) {
      return this.oldToken;
    }
    // Fallback for migration if mode not yet set
    return this.accessToken || this.oldToken;
  }

  /** @Info old token JWT */
  public get oldToken(): string | null {
    return localStorage.getItem(TOKEN_GETTER_KEY) || null;
  }

  public set oldToken(newToken: string | null) {
    if (newToken) {
      localStorage.setItem(TOKEN_TYPE_KEY, TokenType.EXPERT);
      localStorage.setItem(TOKEN_GETTER_KEY, newToken);
    } else {
      localStorage.removeItem(TOKEN_GETTER_KEY);
    }
  }
  /**
   * @deprecated old Login method
   */
  public setTokens(response: AxiosResponse) {
    if (response.headers.hasOwnProperty('authorization')) {
      this.oldToken = response.headers.authorization.replace('Bearer ', '') || null;
    }
  }

  public async logout(lastRoute?: Route) {
    const wasZitadelAuth = this.isZitadelAuthMode || this.authMode === AuthMode.ZITADEL;
    const idTokenHint = this.getStoredZitadelIdToken();
    const logoutHint = this.zitadelLoginName;

    try {
      if (this.isTokenValid && !this.isTokenExpired()) {
        await UserApi.logout();
      }
    } catch (e: unknown) {
      /** @Info we don't want to show error snackbar if the error is 401 because it is expected */
      if ((e as AxiosError)?.response?.status !== 401) {
        Vue.prototype.errorHandler(e, "Error during logout");
      }
    } finally {
      this.saveLastRoute(lastRoute);
      this.clearUserData();
      useModalStore(pinia).closeAllModals();
      await this.handlePostLogoutRedirect(wasZitadelAuth, { idTokenHint, logoutHint });
    }
  }

  /**
   * Clears local session state without calling Zitadel end_session.
   * Used when token renewal fails automatically — keeps the SSO session alive
   * so that /mfa/login can silently re-authenticate via Zitadel.
   * Manual logout (user clicking "Wyloguj") should use logout() instead.
   */
  public softLogout(lastRoute?: Route): void {
    const isZitadel = this.isZitadelAuthMode;
    this.saveLastRoute(lastRoute);
    this.clearUserData();
    useModalStore(pinia).closeAllModals();
    const targetRoute = isZitadel
      ? { name: 'mfaLogout' }
      : { name: Routes.PASSWORD_MANAGEMENT.LOGIN };
    AppRouter.push(targetRoute);
  }

  /** Po zalogowaniu `enterUserPanel` wróci na tę trasę zamiast na stronę startową. */
  public rememberRouteForLogin(route: Route): void {
    this.saveLastRoute(route);
  }

  private saveLastRoute(lastRoute?: Route): void {
    if (lastRoute) {
      localStorage.setItem(LAST_PATH_KEY, lastRoute.fullPath);
    }
  }

  private clearUserData(): void {
    this.resetTablesFilters();
    this.oldToken = null;
    this.accessToken = undefined;
    localStorage.removeItem(TOKEN_TYPE_KEY);
    localStorage.removeItem(ZITADEL_LOGIN_NAME_KEY);
    localStorage.removeItem(AUTH_MODE_KEY);
    sessionStorage.removeItem(ZITADEL_ID_TOKEN_KEY);
    crossTabRenewCoordinator.releaseOwnedLock();
    NotificationService.disconnect();
    this.User = null;
    store.commit('reset');

    try {
      const activityStore = useActivityStore(pinia);
      activityStore.$reset();
    } catch (e) {
      console.warn('ActivityStore reset skipped (Pinia not initialized yet)', e);
    }
  }

  private async handlePostLogoutRedirect(wasZitadelAuth: boolean, args?: { idTokenHint?: string; logoutHint?: string; }): Promise<void> {
    if (wasZitadelAuth) {
      await this.zitadelAuthService.logoutRedirect(args);
    } else {
      GoToService.login();
    }
  }

  private resetTablesFilters() {
    keys(TABLE_FILTER_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  async handleUserState(loginDataResponse: LoginDataResponseDTO) {
    const hasLanguageChanged = loginDataResponse.user.language &&
      loginDataResponse.user.language !== I18N.systemLanguage;
    if (loginDataResponse.user.language && hasLanguageChanged) {
      I18N.setSystemLanguage(loginDataResponse.user.language);
    }
    if (loginDataResponse.activities?.hasExpired) {
      AppRouter.push({ name: Routes.BLOCKED_ACCESS, },);
      return;
    }

    const needsFullReloadForLanguage =
      hasLanguageChanged && !loginDataResponse.user.requirePasswordChange;

    if (needsFullReloadForLanguage) {
      // Cookie is updated; hard-navigate once so the app boots in the new locale
      // instead of painting the dashboard and reloading again after 3s.
      window.location.replace(this.resolvePostLoginHref());
      return;
    }

    this.enterUserPanel();
  }

  /** Target path after login / account switch (LAST_PATH or index route). */
  private resolvePostLoginHref(): string {
    const lastPath = localStorage.getItem(LAST_PATH_KEY);
    if (lastPath) {
      localStorage.removeItem(LAST_PATH_KEY);
      return lastPath;
    }
    return AppRouter.resolve({ name: getIndexRouteName() }).href;
  }

  public enterUserPanel() {
    const lastPath = localStorage.getItem(LAST_PATH_KEY);
    if (lastPath) {
      const pathBeforeChange = AppRouter.currentRoute;
      AppRouter.push({ path: lastPath, });
      window.localStorage.removeItem(LAST_PATH_KEY);
      if (pathBeforeChange.fullPath === lastPath) {
        GoToService.index();
      }
    } else {
      GoToService.index();
    }
  }

  public get isLoggedIn(): boolean {
    return Boolean(this.token);
  }

  /**
   * Use in route guards instead of isLoggedIn or isTokenValid directly.
   * Returns true when the user has a valid or renewable session:
   * - Zitadel mode: access token present OR oidc.storage has a refresh token
   * - Legacy mode: token exists and is not expired
   */
  public get isAuthenticatedForRoute(): boolean {
    return this.isTokenValid && (this.isZitadelAuthMode || !this.isTokenExpired());
  }

  public get User(): Nullable<IUser> {
    return this.UserData;
  }

  public set User(user: Nullable<IUser>) {
    this.UserData = user;
    UserCom.setUserData(user);
    // Format: `uuid|id|organisationId` when logged in, empty when logged out.
    this.userStateV2Key.value = user
      ? [
          user.uuid ?? '',
          user.id != null ? String(user.id) : '',
          user.organisationId != null ? String(user.organisationId) : '',
        ].join('|')
      : '';
  }

  private CurrentRole: Role = Vue.observable(new Role());

  public get isCurrentlyVerifier(): boolean {
    return this.CurrentRole.id === RoleTypes.VERIFIER;
  }

  public get isSystemBlocked(): boolean {
    const activityStore = useActivityStore(pinia);
    const simplePartnerView = this.User?.privileges.simplePartnerView;
    const hasExpiredActivities = activityStore.haveExpiredActivities;

    return !simplePartnerView && hasExpiredActivities;
  }

  get canEditProcedure(): boolean {
    return !!this.User?.isAdminOrVerifier && EnvironmentService.Environment.canEditProcedure;
  }

  get canEditRegulation(): boolean {
    return !!this.User?.isAdminOrVerifier && EnvironmentService.Environment.canEditRegulation;
  }

  public permitSimulation(productType: Nullable<ProductType>): boolean {
    if (!this.User?.privileges) {
      return false;
    }

    switch (productType) {
      case ProductType.MORTGAGE:
        return !this.User.privileges.mortgageLoansDisabled;
      case ProductType.CASH:
        return !this.User.privileges.cashLoansDisabled;
      case ProductType.SME:
        return !this.User.privileges.smeLoansDisabled;
      default:
        return false;
    }
  }

  /** Old auth logic ---start--- */

  /** @Deprecated Old auth logic */
  async logIn(loginData: LoginData) {
    window.localStorage.removeItem(TOKEN_GETTER_KEY);
    const loginDataResponse: LoginDataResponseDTO = await UserApi.login(loginData);
    this.authMode = AuthMode.LEGACY;
    this.handleUserState(loginDataResponse);
  }

  /** @Deprecated Old auth logic */
  public fetchJwtData(): Nullable<JwtData> {
    return this.oldToken ? this.prepareJwtData(this.oldToken) : null;
  }

  /**
   * @Deprecated Use isAuthenticatedForRoute in route guards instead.
   * In Zitadel mode this returns true when either the access token cookie is present
   * OR a stored OIDC session with a refresh token exists — meaning there may be no
   * usable access token yet, but one can be obtained via silent renewal.
   */
  public get isTokenValid(): boolean {
    if (this.isZitadelAuthMode) {
      return !!this.accessToken || this.zitadelAuthService.hasStoredOidcSession();
    }

    if (this.isLegacyAuthMode) {
      return this.oldToken ? this.canDecodeToken(this.oldToken) : false;
    }

    if (this.accessToken) {
      return true;
    }

    if (this.oldToken) {
      return this.canDecodeToken(this.oldToken);
    } else {
      return false;
    }
  }

  /** @Deprecated Old auth logic */
  private setUserData(jwtData: JwtData) {
    if (this.User) {
      merge(this.User, jwtData.user);
    }
    const newRole = jwtData.currentRole || new Role();
    this.CurrentRole.id = newRole.id;
    this.CurrentRole.name = newRole.name;
    if (jwtData.user!.forceLogout) {
      this.logout();
      Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useAuthService.SET_USER_DATA_ERROR_SNACKBAR);
    }
  }

  /**
   * True when `exp` is missing or JWT is past expiry. Used to tell OIDC expiry from API-level 401 on /auth/info.
   */
  public isJwtAccessTokenExpired(token: string | null | undefined): boolean {
    if (!token) {
      return true;
    }

    try {
      const decoded = jwt_decode<JwtExpirationClaim>(token);
      if (typeof decoded.exp !== 'number') {
        return true;
      }

      return decoded.exp <= Math.floor(Date.now() / 1000);
    } catch {
      return true;
    }
  }

  private isZitadelAccessTokenExpired(): boolean {
    return this.isJwtAccessTokenExpired(this.accessToken);
  }

  /** @Deprecated Old auth logic */
  public isTokenExpired = () => {
    if (this.isZitadelAuthMode) {
      return this.isZitadelAccessTokenExpired();
    }

    try {
      if (this.oldToken) {
        const now = new Date().valueOf();
        const date = new Date(0);
        const decoded = this.fetchJwtData();
        date.setUTCSeconds(decoded!.exp);
        return date.valueOf() < now;
      } else {
        return false;
      }
    } catch (err) {
      return false;
    }
  };

  /** @Deprecated Old auth logic */
  public async revokeToken(response: AxiosResponse): Promise<void> {
    if (!this.accessToken && response.headers.hasOwnProperty('authorization')) {
      const token = response.headers.authorization.replace('Bearer ', '');
      if (this.canDecodeToken(token)) {
        this.refreshUserData(token);
      } else {
        const data = {
          token: token,
          response: {
            body: response?.data,
            headers: response?.headers,
            url: response?.config.url,
            status: response?.status,
          },
        };
        Logger.error('Cannot decode token', data);
      }
    }
  }

  /** @Deprecated Old auth logic */
  public canDecodeToken(token: string): boolean {
    if (token && token.length > 0) {
      try {
        jwt_decode(token);
        return true;
      } catch (e) {
        Vue.prototype.errorHandler(e, "Error decoding token");
        return false;
      }
    } else {
      return false;
    }
  }

  /** @Deprecated Old auth logic */
  private prepareJwtData(token: string): Nullable<JwtData> {
    try {
      return new JwtData(jwt_decode(token));
    } catch (e) {
      Logger.error('Occurred error when decoding token and converting to JwtData', { token: token, });
      Vue.prototype.errorHandler(e, "Error decoding token and converting to JwtData");
      return null;
    }
  }

  /** @Deprecated Old auth logic */
  private refreshUserData(token: string) {
    const jwtData = this.prepareJwtData(token);
    if (jwtData) {
      this.oldToken = token;
      this.setUserData(jwtData);
    }
  }
  /** Old auth logic ---end--- */
}

export default new AuthService();
